import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4" }
const _hoisted_2 = { class: "text-lg font-medium" }
const _hoisted_3 = {
  key: 0,
  class: "py-10 px-4 text-center text-gray-500"
}
const _hoisted_4 = {
  key: 1,
  class: "px-4 pb-6 text-gray-500"
}
const _hoisted_5 = {
  key: 2,
  class: "overflow-x-auto"
}
const _hoisted_6 = { class: "w-full text-normal" }
const _hoisted_7 = { class: "border-y border-gray-200 bg-gray-50 text-gray-500" }
const _hoisted_8 = { class: "px-4 py-2 text-left font-medium" }
const _hoisted_9 = { class: "px-2 py-2 text-left font-medium" }
const _hoisted_10 = { class: "px-2 py-2 text-right font-medium" }
const _hoisted_11 = { class: "px-2 py-2 text-right font-medium" }
const _hoisted_12 = { class: "px-2 py-2 text-right font-medium" }
const _hoisted_13 = { class: "px-2 py-2 text-right font-medium" }
const _hoisted_14 = { class: "px-2 py-2 text-left font-medium" }
const _hoisted_15 = { class: "px-4 py-2 text-left font-medium" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "px-4 py-2" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "px-2 py-2 whitespace-nowrap" }
const _hoisted_20 = { class: "px-2 py-2 text-right whitespace-nowrap tabular-nums" }
const _hoisted_21 = { class: "px-2 py-2 text-right whitespace-nowrap tabular-nums" }
const _hoisted_22 = { class: "px-2 py-2 text-right whitespace-nowrap tabular-nums" }
const _hoisted_23 = {
  key: 0,
  class: "text-red-600"
}
const _hoisted_24 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_25 = { class: "px-2 py-2 text-right" }
const _hoisted_26 = { class: "flex flex-col items-end gap-1" }
const _hoisted_27 = { class: "whitespace-nowrap font-semibold tabular-nums" }
const _hoisted_28 = { class: "h-1.5 w-16 overflow-hidden rounded bg-gray-200" }
const _hoisted_29 = { class: "px-2 py-2" }
const _hoisted_30 = { class: "px-4 py-2 whitespace-nowrap" }

import { computed, ref } from 'vue';
import { parseISO } from 'date-fns';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { lotFraction, formatQuantity, PERCENT_TONE_CLASSES } from '@/composables/inventory/inventoryTable';
import { useInventoryFormModal } from '@/composables/inventory/useInventoryFormModal';
import { i18n } from '@/plugins/internationalization/i18n';
import { MedicineInventoryFormId, type MedicineLot, type MedicineLotStatus } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'LotsCard',
  props: {
    lots: {},
    unitName: {}
  },
  setup(__props: any) {

const props = __props;

const { initialState, stepConfig, header, loadingKey, openResource } = useInventoryFormModal();

const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.lots.${key}`, params);
const heading = (key: string) => t(`headings.${key}`);

const showClosed = ref(false);

/** Lots with something left, in the order the ledger draws from them. */
const openLots = computed(() => props.lots.filter((lot) => lot.status === 'active' || lot.status === 'waiting'));
/** Spent or reset lots, newest first. */
const closedLots = computed(() => props.lots.filter((lot) => lot.status === 'used' || lot.status === 'reset').reverse());
const visibleLots = computed(() => (showClosed.value ? [...openLots.value, ...closedLots.value] : openLots.value));

const STATUS_CLASSES: Record<MedicineLotStatus, string> = {
    active: PERCENT_TONE_CLASSES.ok,
    waiting: 'border-transparent bg-gray-100 text-gray-700',
    used: 'border-transparent bg-transparent text-gray-500',
    reset: PERCENT_TONE_CLASSES.warning,
};

const formatDate = (date: string) => i18n.global.d(parseISO(date), 'short');
const withUnit = (value: number) => `${formatQuantity(value)} ${props.unitName}`;

/** "2 × 500 ml" for a purchase made in packs, otherwise just what kind of entry it is. */
function postOf(lot: MedicineLot): string {
    if (lot.source === 'count') {
        return t('post.count');
    }

    if (lot.packs !== null && lot.pack_size !== null) {
        return t('post.packs', { packs: formatQuantity(lot.packs), size: formatQuantity(lot.pack_size), unit: props.unitName });
    }

    return t('post.purchase');
}

function keyOf(lot: MedicineLot): string {
    return `${lot.source}-${lot.record_id}`;
}

const isLoading = computed(() => loadingKey.value !== null);

/** A lot opens the purchase or count it began with. */
function openLot(lot: MedicineLot, open: CallableFunction) {
    if (isLoading.value) {
        return;
    }

    openResource(keyOf(lot), lot.source === 'purchase' ? MedicineInventoryFormId.Purchase : MedicineInventoryFormId.Count, lot.record_id, open);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(MainViewCard, { class: "overflow-hidden" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", _hoisted_2, _toDisplayString(t('heading')), 1),
        (closedLots.value.length)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 0,
              variant: "ghost",
              size: "sm",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showClosed.value = !showClosed.value))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(showClosed.value ? t('hideClosed') : t('showClosed', { count: closedLots.value.length })), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createVNode(ActionModal, {
        "initial-step-index": 1,
        "initial-state": _unref(initialState),
        "step-config": _unref(stepConfig),
        header: _unref(header)
      }, {
        default: _withCtx(({ open }) => [
          (_ctx.lots.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(t('empty')), 1))
            : (visibleLots.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(t('noOpen')), 1))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("table", _hoisted_6, [
                    _createElementVNode("thead", null, [
                      _createElementVNode("tr", _hoisted_7, [
                        _createElementVNode("th", _hoisted_8, _toDisplayString(heading('date')), 1),
                        _createElementVNode("th", _hoisted_9, _toDisplayString(heading('post')), 1),
                        _createElementVNode("th", _hoisted_10, _toDisplayString(heading('quantity')), 1),
                        _createElementVNode("th", _hoisted_11, _toDisplayString(heading('used')), 1),
                        _createElementVNode("th", _hoisted_12, _toDisplayString(heading('reset')), 1),
                        _createElementVNode("th", _hoisted_13, _toDisplayString(heading('remaining')), 1),
                        _createElementVNode("th", _hoisted_14, _toDisplayString(heading('status')), 1),
                        _createElementVNode("th", _hoisted_15, _toDisplayString(heading('batchNumber')), 1)
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleLots.value, (lot) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: keyOf(lot),
                          class: _normalizeClass(["border-b border-gray-100 last:border-b-0 hover:bg-gray-50", isLoading.value ? 'cursor-wait' : 'cursor-pointer']),
                          onClick: ($event: any) => (openLot(lot, open))
                        }, [
                          _createElementVNode("td", _hoisted_17, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "whitespace-nowrap rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700",
                              onClick: _withModifiers(($event: any) => (openLot(lot, open)), ["stop"])
                            }, _toDisplayString(formatDate(lot.date)), 9, _hoisted_18)
                          ]),
                          _createElementVNode("td", _hoisted_19, _toDisplayString(postOf(lot)), 1),
                          _createElementVNode("td", _hoisted_20, _toDisplayString(withUnit(lot.quantity)), 1),
                          _createElementVNode("td", _hoisted_21, _toDisplayString(withUnit(lot.used)), 1),
                          _createElementVNode("td", _hoisted_22, [
                            (lot.reset > 0)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_23, "−" + _toDisplayString(withUnit(lot.reset)), 1))
                              : (_openBlock(), _createElementBlock("span", _hoisted_24, "–"))
                          ]),
                          _createElementVNode("td", _hoisted_25, [
                            _createElementVNode("div", _hoisted_26, [
                              _createElementVNode("span", _hoisted_27, _toDisplayString(withUnit(lot.remaining)), 1),
                              _createElementVNode("div", _hoisted_28, [
                                _createElementVNode("div", {
                                  class: "h-full rounded bg-primary-700",
                                  style: _normalizeStyle({ width: `${_unref(lotFraction)(lot) * 100}%` })
                                }, null, 4)
                              ])
                            ])
                          ]),
                          _createElementVNode("td", _hoisted_29, [
                            _createVNode(_unref(Badge), {
                              variant: "outline",
                              class: _normalizeClass(["whitespace-nowrap font-medium", STATUS_CLASSES[lot.status]])
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(t(`status.${lot.status}`)), 1)
                              ]),
                              _: 2
                            }, 1032, ["class"])
                          ]),
                          _createElementVNode("td", _hoisted_30, _toDisplayString(lot.batch_number ?? '–'), 1)
                        ], 10, _hoisted_16))
                      }), 128))
                    ])
                  ])
                ]))
        ]),
        _: 1
      }, 8, ["initial-state", "step-config", "header"])
    ]),
    _: 1
  }))
}
}

})